import { useEffect } from 'react';

const DOMAIN = 'https://aighostwriterpro.com';
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=1200&q=80&auto=format&fit=crop';

export default function SeoHead({ title, description, keywords, schema, schemas, image, imageAlt, path, article, noindex }) {
  useEffect(() => {
    // Title
    document.title = title || 'AI Ghostwriter Pro — Premium Book Ghostwriting Service';
    
    // Meta description
    setMeta('description', description || 'Hire a professional AI ghostwriter. Transform your ideas into published books. Novels, non-fiction, memoirs, business books. Plans from $149/mo with 7-day free trial.');
    
    // Keywords
    setMeta('keywords', keywords || 'hire a ghostwriter, professional ghostwriting service, AI ghostwriter, book writing service, ghostwriter for hire, novel ghostwriter, non-fiction ghostwriter, memoir ghostwriter');
    
    // Robots
    if (noindex) {
      setMeta('robots', 'noindex, nofollow');
    } else {
      setMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }
    
    // Canonical URL
    const canonicalUrl = path ? `${DOMAIN}${path}` : `${DOMAIN}${window.location.pathname}`;
    setLink('canonical', canonicalUrl);
    
    // Open Graph
    setMeta('og:title', title || 'AI Ghostwriter Pro — Hire a Professional Book Ghostwriter', 'property');
    setMeta('og:description', description || 'Transform your book idea into a professionally written manuscript. Plans from $149/mo.', 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('og:image', image || DEFAULT_IMAGE, 'property');
    setMeta('og:image:alt', imageAlt || 'Professional ghostwriting service for aspiring authors and business professionals', 'property');
    setMeta('og:type', article ? 'article' : 'website', 'property');
    setMeta('og:site_name', 'AI Ghostwriter Pro', 'property');
    setMeta('og:locale', 'en_US', 'property');
    
    // Twitter Card
    setMeta('twitter:card', 'summary_large_image', 'name');
    setMeta('twitter:title', title || 'AI Ghostwriter Pro', 'name');
    setMeta('twitter:description', description || 'Professional AI-powered ghostwriting service.', 'name');
    setMeta('twitter:image', image || DEFAULT_IMAGE, 'name');
    setMeta('twitter:image:alt', imageAlt || 'Professional ghostwriting service for aspiring authors', 'name');
    
    // Article-specific meta
    if (article) {
      setMeta('article:published_time', article.published_at || '', 'property');
      setMeta('article:author', 'AI Ghostwriter Pro', 'property');
      if (article.tags) {
        article.tags.forEach(tag => setMeta('article:tag', tag, 'property'));
      }
    }
    
    // JSON-LD Schema Stacking — supports single schema or array of schemas
    document.querySelectorAll('script[data-seo-schema]').forEach(s => s.remove());
    const allSchemas = schemas || (schema ? [schema] : []);
    allSchemas.forEach((s, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', `schema-${i}`);
      script.textContent = JSON.stringify(s);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-seo-schema]').forEach(s => s.remove());
    };
  }, [title, description, keywords, schema, schemas, image, imageAlt, path, article, noindex]);

  return null;
}

function setMeta(name, content, attr = 'name') {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
